import React, { useEffect, useCallback } from "react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { API } from "aws-amplify";
import { useFormFields } from "libs/fields";
import { onError } from "libs/error";
import { useNavigate } from "react-router-dom";
import { useUser } from "context/AuthContext";
import { SubmitButton, TextInput } from "components/FormElements";
import HttpRequest from "api/HttpRequest";
import Header from "components/Header";
import SubHeader from "components/SubHeader";
import Footer from "components/Footer";
import { Auth } from "aws-amplify";

type Error = {
  response?: { data: { error: string } };
};

export default function Redeem() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { user } = useUser();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<JSX.Element>();
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const [lockCounter, setLockCounter] = useState<number>(0);

  const [fields, handleFieldChange] = useFormFields({
    code: ""
  });

  const onLoad = useCallback(async () => {
    if (user.attributes["custom:lock_count"]) {
      setLockCounter(parseInt(user.attributes["custom:lock_count"]));
    }
    if (user.attributes["custom:lock_time"]) {
      const localLockTime = user.attributes["custom:lock_time"];
      const localTimeLeft = 15 - Math.ceil((Date.now() - localLockTime) / 1000 / 60);
      setTimeLeft(localTimeLeft);
      if (localTimeLeft <= 0) {
        setLockCounter(0);
        setTimeLeft(0);
        await Auth.deleteUserAttributes(user, ["custom:lock_time", "custom:lock_count"]);
      }
    }
  }, [navigate]);

  useEffect(() => {
    onLoad();
  }, [onLoad]);

  function validateForm() {
    return fields.code.length === 19;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();

    setIsLoading(true);

    try {
      if (!user.attributes["custom:blockchain_address"]) {
        const blockchain = await HttpRequest.generateBookchainAddress({
          salt: crypto.randomUUID(),
          email: user.attributes.email
        });
        await Auth.updateUserAttributes(user, {
          "custom:blockchain_address": blockchain.address_hash
        });
      }
      let activation = await API.post("activations", "/activations", {
        body: {
          code: fields.code,
          email: user?.attributes.email,
          language: user.attributes["custom:preferred_language"]
        }
      });
      if (activation.trade) {
        await Auth.deleteUserAttributes(user, ["custom:lock_count"]);
        navigate(`/activation/trade/${activation.cartId}`, { state: { email: user?.attributes.email } });
      } else {
        await API.post("whitelabel", "/buy", {
          body: {
            cartId: activation.cartId,
            orderId: activation.cartId
          }
        });
        await Auth.deleteUserAttributes(user, ["custom:lock_count"]);
        navigate(`/activation/processing/${activation.cartId}`);
      }
    } catch (error: unknown) {
      const e = error as Error;
      fields.code = "";
      const invalidCode = e.response?.data.error === "invalid_code" || "locked_code";
      if (invalidCode) {
        await Auth.updateUserAttributes(user, {
          "custom:lock_count": (lockCounter + 1).toString()
        });
      }
      const localLockCounter = lockCounter + 1;
      setLockCounter(localLockCounter);
      if (localLockCounter >= 5) {
        const timestamp = Date.now();
        await Auth.updateUserAttributes(user, {
          "custom:lock_time": timestamp.toString()
        });
        setTimeLeft(15 - Math.ceil((Date.now() - timestamp) / 1000 / 60));
      }
      const errorHandler = onError(e);
      setErrorMessage(errorHandler);
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <SubHeader heading={t("local.redeem.title.main").toString()} />
      <main className="px-4 md:px-0 flex-grow bg-ct-lightgrey">
        <form className="container mx-auto max-w-md flex flex-col justify-center space-y-4 pb-20" onSubmit={handleSubmit}>
          <h2 className="text-3xl text-black text-center pt-20 mb-2.5">{t("local.redeem.section.main")}</h2>
          <p className="bodyText text-black">{t("local.redeem.activation.section.instruction")}</p>
          <TextInput
            handleChange={handleFieldChange}
            name="code"
            value={fields.code}
            label={t("local.redeem.activation.label.code")}
            pattern="[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}-[A-Z0-9]{4}"
          />
          <SubmitButton disabled={!validateForm() || lockCounter >= 5} loading={isLoading} label={t("global.button.redeem")} />
          {lockCounter >= 5 && (
            <div>
              <h3 className="text-3xl text-ct-error text-center">
                <i className="icon-ban text-2xl mr-1"></i>
                {t("local.redeem.activation.error.toomany.main")}
              </h3>
              <p>{t("local.redeem.activation.error.toomany.explanation", { minutes: timeLeft })}</p>
            </div>
          )}
          {errorMessage}
        </form>
      </main>
      <Footer />
    </div>
  );
}
